import { CSSProperties, MutableRefObject, RefObject, useCallback, useEffect, useState } from 'react'
import { StoreSnapshot } from '@tldraw/tldraw'
import Styles from './DesignContentPreviewResizeWrapper.module.scss'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { Ref } from 'components/DesignContentPreview/Types'
import { useResizeObserver } from 'beautiful-react-hooks'
import { LoadingState } from 'components/DesignContentPreview/Types'
import cx from 'classnames'

export interface DesignContentPreviewResizeWrapperProps {
  width: number
  height: number
  designContent: StoreSnapshot<any>
  containerWidth?: number
  containerRef?: RefObject<HTMLDivElement>
  onLoadingStateChange?: (loadingState: LoadingState) => void
  className?: string
  autoplayVideo?: boolean
}

export const DesignContentPreviewResizeWrapper = ({
  width,
  height,
  designContent,
  containerWidth,
  containerRef,
  onLoadingStateChange,
  className,
  autoplayVideo = true,
}: DesignContentPreviewResizeWrapperProps) => {
  const containerRefWidth = useResizeObserver(containerRef as MutableRefObject<HTMLDivElement>)?.width
  const actualContainerWidth = containerWidth || containerRefWidth

  const [editorRef, setEditorRef] = useState<Ref | null>(null)

  const resizeDesign = useCallback(() => {
    const timeouts = [250, 500, 1000]

    if (editorRef) {
      editorRef.zoomToFit()

      // it can take a few milliseconds for the container's viewport to actually be resized, so adding a few re-tries for good measure
      timeouts.forEach((delay: number) => {
        setTimeout(() => {
          editorRef.zoomToFit()
        }, delay)
      })
    }
  }, [editorRef])

  // resize the design when its container's width changes
  useEffect(() => {
    resizeDesign()
  }, [actualContainerWidth, resizeDesign])

  // resize the design when the window is resized
  useEffect(() => {
    window.addEventListener('resize', resizeDesign)

    return () => window.removeEventListener('resize', resizeDesign)
  }, [resizeDesign])

  const styles = {
    '--aspect-ratio': `${height} / ${width}`,
    '--container-width': `${actualContainerWidth}px`,
  } as CSSProperties

  return (
    <div className={cx(Styles.designContentContainer, className)} style={styles}>
      <DesignContentPreview
        content={designContent}
        ref={setEditorRef}
        onLoadingStateChange={onLoadingStateChange}
        autoplayVideo={autoplayVideo}
      />
    </div>
  )
}
